import {useEffect, useState} from 'react'
import axios from 'axios'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import {useReduxAuth} from '../../../auth'
import {useAppDispatch} from '../../../../../store/hooks'
import {setUser} from '../../../../../store/slices/authSlice'
import {getAuth} from '../../../auth/core/AuthHelpers'
import {toast} from 'react-toastify'
import Loader from '../../components/Loader'
import ConfirmationModal from '../../components/modals/ConfirmationModal'
import SubscriptionPlan from '../../components/stripe-payments/SubscriptionPlan'
import BillingDetails from '../../components/stripe-payments/BillingDetails'
import {
  isUserActive,
  isUserOnTrial,
  isUserCancelled,
  isUserInactive,
  isUserEverSubscribed,
  isUserTerminated,
  isUserFailed,
  isUserPastDue,
} from '../../helper/userHelpers'
import {currentMonthYear} from '../../helper/dateTimeHelpers'
import {handleApiError} from '../../helper/errorHandlers'
import {fetchSignedUrl} from '../../helper/helperFunction'

export default function SubscriptionPage() {
  const {currentUser} = useReduxAuth()
  const dispatch = useAppDispatch()

  // State management
  const [isCancelling, setIsCancelling] = useState(false)
  const [isBillingOverviewLoading, setIsBillingOverviewLoading] = useState(true)
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [activePatientCount, setActivePatientCount] = useState(0)
  const [deviceCount, setDeviceCount] = useState(0)

  // const notifyError = (msg) => toast.error(msg)
  const notifySuccess = (msg) => toast.success(msg)

  // OPTIMIZED: Single API call to refresh user data
  const refreshUserData = async () => {
    try {
      const authData = getAuth()
      if (!authData?.token) return

      const {data} = await axios.get(API_ENDPOINTS.AUTH_ME(), {
        headers: {
          Authorization: `Bearer ${authData.token}`
        }
      })
      
      if (data?.data) {  // Fixed: user data is in data.data, not data.data.user
        let updatedUser = data.data

        // Handle profile image if exists
        if (updatedUser.profile_image) {
          try {
            const presignedURL = await fetchSignedUrl(updatedUser.profile_image)
            updatedUser = {...updatedUser, profile_image_pre_signed: presignedURL}
          } catch (err) {
            // Continue without image if fetch fails
          }
        }

        // Update Redux state with latest user data
        dispatch(setUser(updatedUser))
        return updatedUser
      }
    } catch (error) {
      console.error('Error refreshing user data:', error)
      return null
    }
  }


  const fetchActivePatientCount = async () => {
    try {
      const res = await axios.get(API_ENDPOINTS.GET_ACTIVE_PATIENTS_CURRENT_USER(currentMonthYear),
        {params: { status: "active" }})
      const activePatients = (res?.data?.data?.patients || []).filter((p) => p.status === 1)
      setActivePatientCount(activePatients.length)
    } catch (error) {
      handleApiError(error, {
        operation: 'Fetch active patient count',
        silent: true,
        onError: () => setActivePatientCount(0),
      })
    }
  }

  const fetchDeviceCount = async () => {
    try {
      if (!currentUser?.id) return
      const res = await axios.get(API_ENDPOINTS.COUNT_DOCTOR_RENTAL_DEVICES(currentUser.id))
      setDeviceCount(res?.data?.message?.userDevices || 0)
    } catch (error) {
      handleApiError(error, {
        operation: 'Fetch device count',
        silent: true,
        onError: () => setDeviceCount(0),
      })
    }
  }

  // OPTIMIZED: Fast Stripe return detection with immediate updates
  useEffect(() => {
    let hasRefreshed = false
    let lastRefreshTime = 0
    const MINIMUM_REFRESH_INTERVAL = 10000 // Reduced to 10 seconds for faster updates

    const handleStripeReturn = async (source = 'unknown') => {
      const now = Date.now()
      
      // Only refresh if Stripe flag exists
      const wasOnStripePortal = sessionStorage.getItem('stripe_portal_opened')
      if (!wasOnStripePortal) return
      
      // Lighter debouncing - allow refresh if enough time passed
      if (hasRefreshed && (now - lastRefreshTime < MINIMUM_REFRESH_INTERVAL)) {
        return
      }

      hasRefreshed = true
      lastRefreshTime = now
      
      try {
        // Show loading immediately for instant feedback
        setIsLoading(true)
        
        // Priority 1: Update user data first for immediate UI response
        const updatedUser = await refreshUserData()
        
        // Priority 2: Update counts in background (non-blocking)
        setTimeout(async () => {
          try {
            await Promise.all([
              fetchActivePatientCount(),
              fetchDeviceCount()
            ])
          } catch (error) {
            console.error('Error updating counts:', error)
          }
        }, 100)
        
        toast.success('Subscription status updated!')
        
      } catch (error) {
        console.error('❌ Error processing Stripe return:', error)
        toast.error('Failed to update subscription status')
      } finally {
        // Remove loading state quickly to show updated UI
        setTimeout(() => setIsLoading(false), 500)
        sessionStorage.removeItem('stripe_portal_opened')
        
        // Reset debounce after shorter interval
        setTimeout(() => { hasRefreshed = false }, MINIMUM_REFRESH_INTERVAL)
      }
    }

    // Immediate event handlers for faster response
    const handleWindowFocus = () => {
      if (!document.hidden) {
        // Reduced delay for faster response
        setTimeout(() => handleStripeReturn('window-focus'), 100)
      }
    }

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Reduced delay for faster response
        setTimeout(() => handleStripeReturn('visibility-change'), 100)
      }
    }

    // Set up event listeners
    window.addEventListener('focus', handleWindowFocus, { passive: true })
    document.addEventListener('visibilitychange', handleVisibilityChange, { passive: true })

    // Check on mount if flag exists - immediate response
    if (sessionStorage.getItem('stripe_portal_opened')) {
      setTimeout(() => handleStripeReturn('page-load'), 200)
    }

    // Cleanup
    return () => {
      window.removeEventListener('focus', handleWindowFocus)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  // OPTIMIZED: Single initialization effect with smart data fetching
  useEffect(() => {
    const initializeComponent = async () => {
      try {
        setIsLoading(true)
        setIsBillingOverviewLoading(true)

        // Always refresh user data first to get latest subscription status
        const updatedUser = await refreshUserData()
        const userToCheck = updatedUser || currentUser

        if (userToCheck && (
          isUserActive(userToCheck) ||
          isUserCancelled(userToCheck) ||
          isUserOnTrial(userToCheck) ||
          isUserPastDue(userToCheck) ||
          isUserEverSubscribed(userToCheck)
        )) {
          // Batch all data fetching for better performance
          await Promise.all([
            fetchActivePatientCount(),
            fetchDeviceCount()
          ])
        } else {
          // Still fetch counts for non-subscribed users
          await Promise.all([
            fetchActivePatientCount(),
            fetchDeviceCount()
          ])
        }
      } catch (error) {
        console.error('Error initializing component:', error)
      } finally {
        setIsLoading(false)
        setIsBillingOverviewLoading(false)
      }
    }
    
    initializeComponent()
  }, []) // Empty dependency - runs once on mount

  const cancelSubscription = async () => {
    try {
      setIsCancelling(true)
      const res = await axios.put(API_ENDPOINTS.CANCEL_SUBSCRIPTION())
      if (res?.status === 200) {
        notifySuccess('Subscription cancelled successfully!')
        // Refresh data after cancellation
        await refreshUserData()
      }
    } catch (error) {
      handleApiError(error, 'Cancel subscription')
    } finally {
      setIsCancelling(false)
      setIsCancelModalOpen(false)
    }
  }

  const resumeSubscription = async () => {
    try {
      setIsLoading(true)
      const res = await axios.put(API_ENDPOINTS.RESUME_SUBSCRIPTION())
      if (res?.status === 200) {
        notifySuccess('Subscription resumed successfully!')
        // Refresh data after resumption
        await refreshUserData()
      }
    } catch (error) {
      handleApiError(error, 'Resume subscription')
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading && isBillingOverviewLoading) {
    return <Loader />
  }

  return (
        <div className="d-flex flex-column">
          {/* Billing Details Component */}
          {(isUserActive(currentUser) || 
            isUserCancelled(currentUser) || 
            isUserOnTrial(currentUser) || 
            isUserPastDue(currentUser) || 
            isUserEverSubscribed(currentUser) ||
            isUserTerminated(currentUser) ||
            isUserInactive(currentUser) ||
            isUserFailed(currentUser) ||
            currentUser?.stripe_customer_id // Show if they have Stripe customer ID
          ) && (
            <BillingDetails
              key={`billing-${currentUser?.id}-${currentUser?.subscription_status}-${currentUser?.updated_at}`}
              currentUser={currentUser}
              isLoading={isBillingOverviewLoading}
              isActive={isUserActive(currentUser)}
              isCancelled={isUserCancelled(currentUser)}
              isFailed={isUserFailed(currentUser)}
              isCancelling={isCancelling}
              openCancelModal={() => setIsCancelModalOpen(true)}
              subscriptionEndDate={null}
              activePatientCount={activePatientCount}
              deviceCount={deviceCount}
            />
          )}

          {/* Subscription Plan Component */}
          <div className='d-flex gap-8 justify-content-center mt-5'>
            <SubscriptionPlan 
              title="Patient Services"
              features={[
                "$10 / patient / month",
                "Billed monthly",
                "Cancel anytime",
                "No contract",
                "HIPAA-compliant platform",
                "Free technical support",
                "Free staff accounts",
                "Real-time alerts",
                "Auto time-tracking",
                "Free feature updates",
              ]}
              currentUser={currentUser} 
              activePatientCount={activePatientCount}
              onCancelClick={() => setIsCancelModalOpen(true)}
              onResumeClick={resumeSubscription}
              isLoading={isLoading}
            />

            <SubscriptionPlan 
              title="Monitoring Device"
              features={[
                "Purchase or Rent device",
                "Rent starting at just $8 / month",
                "Billed monthly",
                "No contract or setup fees",
                "Pre-configured cellular device",
                "ZERO Patient or Staff training required",
                "Fully integrated with CandiHealth",
                "Unlimited data transmission",
                "Automated syncing of health data",
              ]}
              currentUser={currentUser} 
              activePatientCount={activePatientCount}
              onCancelClick={() => setIsCancelModalOpen(true)}
              onResumeClick={resumeSubscription}
              isLoading={isLoading}
            />

          </div>


          {/* Cancel Subscription Modal */}
          <ConfirmationModal
            isOpen={isCancelModalOpen}
            onClose={() => setIsCancelModalOpen(false)}
            onConfirm={cancelSubscription}
            title="Cancel Subscription"
            message="Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your current billing period."
            confirmText="Yes, Cancel Subscription"
            cancelText="Keep Subscription"
            isLoading={isCancelling}
            variant="danger"
          />
        </div>
  )
}
